import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ExpenseInput from '../components/ExpenseInput';
import ExpenseList from '../components/ExpenseList';
import AudioExpenseRecorder from '../components/AudioExpenseRecorder';
import InvoiceUploader from '../components/InvoiceUploader';
import expensesApi from '../api/expenses';
import { usersApi } from '../api/users';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_LABELS } from '../types/user';
import { TransactionType } from '../types/expense';

type InputTab = 'text' | 'audio' | 'invoice';

const Dashboard: React.FC = () => {
  const { user, logout, isAdmin, isOperator } = useAuth();
  const [expenseRefresh, setExpenseRefresh] = useState(0);
  const [ddsLoading, setDdsLoading] = useState(false);
  const [folderLoading, setFolderLoading] = useState(false);
  const [ddsMessage, setDdsMessage] = useState<{ type: 'success' | 'error'; text: string; url?: string } | null>(null);
  const [activeTab, setActiveTab] = useState<InputTab>('text');
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [alertDismissed, setAlertDismissed] = useState(false);
  const [listType, setListType] = useState<TransactionType>('expense');
  const [trashCount, setTrashCount] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    const abortController = new AbortController();

    usersApi.getCompanyProfile(abortController.signal)
      .then((profile) => {
        if (!abortController.signal.aborted) {
          setProfileIncomplete(!profile.unp);
        }
      })
      .catch(() => {});

    return () => abortController.abort();
  }, [isAdmin]);

  // Fetch trash count for badge
  useEffect(() => {
    const abortController = new AbortController();
    expensesApi.getTrashed(1, 1, abortController.signal)
      .then((res) => { if (!abortController.signal.aborted) setTrashCount(res.total); })
      .catch(() => {});
    return () => abortController.abort();
  }, [expenseRefresh]);

  const handleExpenseSaved = () => {
    setExpenseRefresh((prev) => prev + 1);
  };

  const handleOpenFinanceFolder = async () => {
    // Open a blank tab synchronously so popup blockers do not block it
    // when we navigate after the async request completes.
    const newTab = window.open('', '_blank');
    setFolderLoading(true);
    setDdsMessage(null);

    try {
      const result = await expensesApi.getFinanceFolder();
      if (newTab) {
        newTab.location.href = result.folderUrl;
      } else {
        window.open(result.folderUrl, '_blank');
      }
    } catch (error: any) {
      if (newTab) newTab.close();
      setDdsMessage({
        type: 'error',
        text: error.response?.data?.message || 'Ошибка при открытии папки',
      });
      setTimeout(() => setDdsMessage(null), 5000);
    } finally {
      setFolderLoading(false);
    }
  };

  const handleGenerateDds = async () => {
    setDdsLoading(true);
    setDdsMessage(null);

    try {
      const result = await expensesApi.generateDds();
      setDdsMessage({
        type: 'success',
        text: result.created ? 'ДДС создан по актуальным данным' : 'ДДС обновлён по актуальным данным',
        url: result.documentUrl,
      });
      // Don't auto-open the sheet — the success notification has an "Открыть ДДС" link.
    } catch (error: any) {
      setDdsMessage({
        type: 'error',
        text: error.response?.data?.message || 'Ошибка при генерации ДДС',
      });
      setTimeout(() => setDdsMessage(null), 5000);
    } finally {
      setDdsLoading(false);
    }
  };

  const tabs = [
    { id: 'text' as InputTab, label: 'Текст', icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    )},
    { id: 'audio' as InputTab, label: 'Голос', icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    )},
    { id: 'invoice' as InputTab, label: 'Инвойс', icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )},
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center flex-wrap gap-2 sm:gap-3">
            {/* Title — full width on small screens, inline on md+ */}
            <h1 className="text-lg sm:text-2xl font-bold text-gray-800 w-full md:w-auto">Fin Calendar</h1>

            {/* Action buttons */}
            {!isOperator && (
              <>
                <button
                  onClick={handleOpenFinanceFolder}
                  disabled={folderLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {folderLoading ? (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                  )}
                  <span className="hidden sm:inline">{folderLoading ? 'Загрузка...' : 'Фин. документы'}</span>
                </button>
                <button
                  onClick={handleGenerateDds}
                  disabled={ddsLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {ddsLoading ? (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  <span className="hidden sm:inline">{ddsLoading ? 'Генерация...' : 'Обновить ДДС'}</span>
                </button>
              </>
            )}
            {isAdmin && (
              <Link
                to="/users"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                <span className="hidden sm:inline">Пользователи</span>
              </Link>
            )}

            {/* User section — pushed to the right */}
            <div className="flex items-center gap-2 sm:gap-3 ml-auto">
              <Link
                to="/profile"
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                title="Профиль компании"
              >
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded-full"
                  />
                ) : (
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xs sm:text-sm">
                    {user?.name?.charAt(0) || '?'}
                  </div>
                )}
                <div className="hidden lg:block">
                  <span className="text-sm text-gray-700 block">{user?.name}</span>
                  {user?.role && (
                    <span className="text-xs text-gray-400">{ROLE_LABELS[user.role]}</span>
                  )}
                </div>
              </Link>
              <Link
                to="/profile"
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                title="Профиль компании"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </Link>
              <Link
                to="/trash"
                className="relative p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                title="Корзина"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14zM6 7v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6zm8 7v4h-4v-4H8l4-4 4 4h-2z" />
                </svg>
                {trashCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none">
                    {trashCount > 99 ? '99+' : trashCount}
                  </span>
                )}
              </Link>
              <button
                onClick={logout}
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                title="Выйти"
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
          {ddsMessage && (
            <div
              className={`mt-3 p-2 rounded-md text-sm flex items-center justify-between ${
                ddsMessage.type === 'success'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              <span>{ddsMessage.text}</span>
              <div className="flex items-center gap-2">
                {ddsMessage.url && (
                  <a
                    href={ddsMessage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-green-900"
                  >
                    Открыть ДДС
                  </a>
                )}
                <button
                  onClick={() => setDdsMessage(null)}
                  className="p-0.5 hover:opacity-70"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Profile incomplete alert — admin only */}
      {isAdmin && profileIncomplete && !alertDismissed && (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 mt-3 sm:mt-4">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <svg className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1 text-sm text-amber-800">
              Добавьте данные вашей фирмы в Профиле для автоматического определения документов, что это Доход либо Расход. В противном случае всё будет распознаваться как расход.
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to="/profile"
                className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-md hover:bg-amber-700 transition-colors"
              >
                Заполнить профиль
              </Link>
              <button
                onClick={() => setAlertDismissed(true)}
                className="p-1 text-amber-400 hover:text-amber-600 transition-colors"
                title="Закрыть"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Input Methods with Tabs */}
          <div className="bg-white rounded-lg shadow-md">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'text' && <ExpenseInput onSaved={handleExpenseSaved} />}
              {activeTab === 'audio' && <AudioExpenseRecorder onSaved={handleExpenseSaved} />}
              {activeTab === 'invoice' && <InvoiceUploader onSaved={handleExpenseSaved} />}
            </div>
          </div>

          {/* Right Column - Expense/Income List */}
          <div>
            <div className="flex mb-4 bg-white rounded-lg shadow-md overflow-hidden">
              <button
                onClick={() => setListType('expense')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                  listType === 'expense'
                    ? 'bg-red-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Расходы
              </button>
              <button
                onClick={() => setListType('income')}
                className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                  listType === 'income'
                    ? 'bg-green-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Доходы
              </button>
            </div>
            <ExpenseList refreshTrigger={expenseRefresh} type={listType} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
